import {
  isHalfWidthNumeric,
  isHalfOrFullWidthNumeric,
  FieldValidationResult,
  ValidationError,
} from './common-validation';
import {
  validateGaliCode,
  validateRequired,
  validateKatakana,
  validateRepresentatives,
} from './company-shared-validation';
import { VALIDATION_MESSAGES, CUSTOMER_MESSAGES } from './validation-messages';

import type {
  CustomerRegistrationForm,
  CustomerDetailForm,
  Address,
  HeadOfficeAddress,
  NoharaRepresentative,
} from '../../types/company.types';

// Individual field validations
export const validateGaliCustomerCode = (code: string): FieldValidationResult => {
  return validateGaliCode(code, VALIDATION_MESSAGES.GALI_CODE_FORMAT);
};

export const validateOfficialName = (name: string): FieldValidationResult => {
  return validateRequired(name, VALIDATION_MESSAGES.OFFICIAL_NAME_REQUIRED);
};

export const validateAbbreviatedName = (name: string): FieldValidationResult => {
  return validateRequired(name, VALIDATION_MESSAGES.ABBREVIATED_NAME_REQUIRED);
};

export const validateBranchDepartment = (branch: string): FieldValidationResult => {
  if (branch && branch.length > 100) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.BRANCH_DEPARTMENT_MAX_LENGTH,
    };
  }

  return { isValid: true };
};

export const validateCustomerNameKatakana = (name: string): FieldValidationResult => {
  return validateKatakana(
    name,
    CUSTOMER_MESSAGES.CUSTOMER_NAME_KATAKANA_REQUIRED,
    CUSTOMER_MESSAGES.CUSTOMER_NAME_KATAKANA_FORMAT
  );
};

// Address validation
// Helper to validate postal code parts
const validatePostalCodePart = (
  value: string | undefined,
  prefix: string,
  part: 'postalCode1' | 'postalCode2',
  expectedLength: number,
  errors: ValidationError[]
): void => {
  const requiredMessage =
    part === 'postalCode1'
      ? VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE1_REQUIRED
      : VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE2_REQUIRED;

  const formatMessage =
    part === 'postalCode1'
      ? VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE1_FORMAT
      : VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE2_FORMAT;

  const halfWidthErrorMessage =
    part === 'postalCode1'
      ? VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE1_HALF_WIDTH
      : VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE2_HALF_WIDTH;

  if (!value?.trim()) {
    errors.push({ field: `${prefix}.${part}`, message: requiredMessage });
    return;
  }

  if (!isHalfWidthNumeric(value)) {
    errors.push({
      field: `${prefix}.${part}`,
      message: halfWidthErrorMessage,
    });
    return;
  }

  if (value.length !== expectedLength) {
    errors.push({ field: `${prefix}.${part}`, message: formatMessage });
  }
};

export const validateAddress = (address: Address, prefix: string): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Postal Code validation
  validatePostalCodePart(address.postalCode1, prefix, 'postalCode1', 3, errors);
  validatePostalCodePart(address.postalCode2, prefix, 'postalCode2', 4, errors);

  // Phone validation
  if (address.phone && !isHalfOrFullWidthNumeric(address.phone)) {
    errors.push({
      field: `${prefix}.phone`,
      message: VALIDATION_MESSAGES.PHONE_FORMAT,
    });
  }

  // Fax validation
  if (address.fax && !isHalfOrFullWidthNumeric(address.fax)) {
    errors.push({
      field: `${prefix}.fax`,
      message: VALIDATION_MESSAGES.FAX_FORMAT,
    });
  }

  return errors;
};

// Head office address validation (without phone/fax)
export const validateHeadOfficeAddress = (
  address: HeadOfficeAddress,
  prefix: string
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Postal Code validation - only check required fields when empty
  if (!address.postalCode1?.trim()) {
    errors.push({
      field: `${prefix}.postalCode1`,
      message: VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE1_REQUIRED,
    });
  } else if (!isHalfWidthNumeric(address.postalCode1)) {
    errors.push({
      field: `${prefix}.postalCode1`,
      message: VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE1_HALF_WIDTH,
    });
  } else if (address.postalCode1.length !== 3) {
    errors.push({
      field: `${prefix}.postalCode1`,
      message: VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE1_FORMAT,
    });
  }

  if (!address.postalCode2?.trim()) {
    errors.push({
      field: `${prefix}.postalCode2`,
      message: VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE2_REQUIRED,
    });
  } else if (!isHalfWidthNumeric(address.postalCode2)) {
    errors.push({
      field: `${prefix}.postalCode2`,
      message: VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE2_HALF_WIDTH,
    });
  } else if (address.postalCode2.length !== 4) {
    errors.push({
      field: `${prefix}.postalCode2`,
      message: VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE2_FORMAT,
    });
  }

  return errors;
};

// Representatives validation - using shared function from company-shared-validation

// Main form validation
// Helper to validate a single validation result
const addValidationResult = (
  result: FieldValidationResult,
  field: string,
  errors: ValidationError[]
): void => {
  if (!result.isValid) {
    errors.push({ field, message: result.error });
  }
};

// Helper to check required fields
const addRequiredFieldError = (
  value: string | undefined,
  field: string,
  message: string,
  errors: ValidationError[]
): void => {
  if (!value?.trim()) {
    errors.push({ field, message });
  }
};

// Helper to validate required address fields
const validateRequiredAddressFields = (
  address: Address | HeadOfficeAddress,
  prefix: string,
  messages: {
    postalCode1: string;
    postalCode2: string;
    address1: string;
    address2: string;
    phone?: string;
    fax?: string;
  },
  errors: ValidationError[]
): void => {
  addRequiredFieldError(address.postalCode1, `${prefix}.postalCode1`, messages.postalCode1, errors);
  addRequiredFieldError(address.postalCode2, `${prefix}.postalCode2`, messages.postalCode2, errors);
  addRequiredFieldError(address.address1, `${prefix}.address1`, messages.address1, errors);
  addRequiredFieldError(address.address2, `${prefix}.address2`, messages.address2, errors);

  if (messages.phone && 'phone' in address) {
    addRequiredFieldError(address.phone, `${prefix}.phone`, messages.phone, errors);
  }
  if (messages.fax && 'fax' in address) {
    addRequiredFieldError(address.fax, `${prefix}.fax`, messages.fax, errors);
  }
};

export const validateCustomerRegistrationForm = (
  form: CustomerRegistrationForm
): { isValid: boolean; errors: ValidationError[] } => {
  const errors: ValidationError[] = [];

  // Basic company info validation
  addValidationResult(
    validateGaliCustomerCode(form.galiCustomerCode || ''),
    'galiCustomerCode',
    errors
  );
  addValidationResult(validateOfficialName(form.officialName), 'officialName', errors);
  addValidationResult(validateAbbreviatedName(form.abbreviatedName), 'abbreviatedName', errors);
  addValidationResult(
    validateCustomerNameKatakana(form.customerNameKatakana || ''),
    'customerNameKatakana',
    errors
  );

  // Address validation
  errors.push(
    ...validateAddress(form.currentAddress, 'currentAddress'),
    ...validateHeadOfficeAddress(form.headOfficeAddress, 'headOfficeAddress'),
    ...validateRepresentatives(form.representatives)
  );

  // Required field validations for registration
  addRequiredFieldError(
    form.officialName,
    'officialName',
    VALIDATION_MESSAGES.OFFICIAL_NAME_REQUIRED,
    errors
  );
  addRequiredFieldError(
    form.abbreviatedName,
    'abbreviatedName',
    VALIDATION_MESSAGES.ABBREVIATED_NAME_REQUIRED,
    errors
  );
  addRequiredFieldError(
    form.customerNameKatakana,
    'customerNameKatakana',
    VALIDATION_MESSAGES.COMPANY_NAME_KANA_REQUIRED,
    errors
  );

  if (!form.customerCategory) {
    errors.push({
      field: 'customerCategory',
      message: CUSTOMER_MESSAGES.CUSTOMER_CATEGORY_REQUIRED,
    });
  }

  // Current address required fields
  validateRequiredAddressFields(
    form.currentAddress,
    'currentAddress',
    {
      postalCode1: VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE1_REQUIRED,
      postalCode2: VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE2_REQUIRED,
      address1: VALIDATION_MESSAGES.CURRENT_ADDRESS_ADDRESS1_REQUIRED,
      address2: VALIDATION_MESSAGES.CURRENT_ADDRESS_ADDRESS2_REQUIRED,
      phone: VALIDATION_MESSAGES.PHONE_REQUIRED,
      fax: VALIDATION_MESSAGES.FAX_REQUIRED,
    },
    errors
  );

  // Head office address required fields
  validateRequiredAddressFields(
    form.headOfficeAddress,
    'headOfficeAddress',
    {
      postalCode1: VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE1_REQUIRED,
      postalCode2: VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE2_REQUIRED,
      address1: VALIDATION_MESSAGES.HEAD_OFFICE_ADDRESS1_REQUIRED,
      address2: VALIDATION_MESSAGES.HEAD_OFFICE_ADDRESS2_REQUIRED,
    },
    errors
  );

  // At least one representative is required
  const validRepresentatives = form.representatives.filter(
    (rep: NoharaRepresentative) => rep.department && rep.salesPerson
  );
  if (validRepresentatives.length === 0) {
    errors.push({
      field: 'representatives',
      message: VALIDATION_MESSAGES.REPRESENTATIVES_MIN_REQUIRED,
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Customer detail form validation
export const validateCustomerDetailForm = (
  form: CustomerDetailForm
): { isValid: boolean; errors: ValidationError[] } => {
  const errors: ValidationError[] = [];

  // Basic company info validation using existing validators
  addValidationResult(
    validateGaliCustomerCode(form.galiAccountCode || ''),
    'galiAccountCode',
    errors
  );
  addValidationResult(validateOfficialName(form.formalName), 'formalName', errors);
  addValidationResult(validateAbbreviatedName(form.abbreviationName), 'abbreviationName', errors);

  // Validate katakana and require it for detail form
  if (!form.partnerNameKana?.trim()) {
    errors.push({
      field: 'partnerNameKana',
      message: VALIDATION_MESSAGES.COMPANY_NAME_KANA_REQUIRED,
    });
  } else {
    addValidationResult(
      validateCustomerNameKatakana(form.partnerNameKana),
      'partnerNameKana',
      errors
    );
  }

  // Address validation using existing validators
  errors.push(
    ...validateAddress(form.currentAddress, 'currentAddress'),
    ...validateHeadOfficeAddress(form.headOfficeAddress, 'headOfficeAddress'),
    ...validateRepresentatives(form.representatives)
  );

  // Required field validations for detail form
  addRequiredFieldError(
    form.formalName,
    'formalName',
    VALIDATION_MESSAGES.OFFICIAL_NAME_REQUIRED,
    errors
  );
  addRequiredFieldError(
    form.abbreviationName,
    'abbreviationName',
    VALIDATION_MESSAGES.ABBREVIATED_NAME_REQUIRED,
    errors
  );

  // Customer category is required
  if (!form.customerCategory) {
    errors.push({
      field: 'customerCategory',
      message: CUSTOMER_MESSAGES.CUSTOMER_CATEGORY_REQUIRED,
    });
  }

  // Current address required fields
  validateRequiredAddressFields(
    form.currentAddress,
    'currentAddress',
    {
      postalCode1: VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE1_REQUIRED,
      postalCode2: VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE2_REQUIRED,
      address1: VALIDATION_MESSAGES.CURRENT_ADDRESS_ADDRESS1_REQUIRED,
      address2: VALIDATION_MESSAGES.CURRENT_ADDRESS_ADDRESS2_REQUIRED,
      phone: VALIDATION_MESSAGES.PHONE_REQUIRED,
      fax: VALIDATION_MESSAGES.FAX_REQUIRED,
    },
    errors
  );

  // Head office address required fields
  validateRequiredAddressFields(
    form.headOfficeAddress,
    'headOfficeAddress',
    {
      postalCode1: VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE1_REQUIRED,
      postalCode2: VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE2_REQUIRED,
      address1: VALIDATION_MESSAGES.HEAD_OFFICE_ADDRESS1_REQUIRED,
      address2: VALIDATION_MESSAGES.HEAD_OFFICE_ADDRESS2_REQUIRED,
    },
    errors
  );

  // At least one representative is required
  const validRepresentatives = form.representatives.filter(
    (rep: NoharaRepresentative) => rep.department && rep.salesPerson
  );
  if (validRepresentatives.length === 0) {
    errors.push({
      field: 'representatives',
      message: VALIDATION_MESSAGES.REPRESENTATIVES_MIN_REQUIRED,
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
